import React, { useEffect } from 'react'
import { HelmetProvider } from 'react-helmet-async';
import { MotionConfig } from 'framer-motion'
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom'
import { useLocale } from './contexts/LocaleContext';
import { LocaleProvider } from './contexts/LocaleContext'
import { useScrollToAnchor } from './hooks/useScrollToAnchor'
import SEO from './components/SEO'
import Header from './components/Header'
import Hero from './components/Hero'
import Features from './components/Features'
import Screenshots from './components/Screenshots'
import Testimonials from './components/Testimonials'
import Blog from './components/Blog'
import BlogPost from './components/BlogPost'
import Contact from './components/Contact'
import DeleteAccount from './components/DeleteAccount'
import FAQ from './components/FAQ'
import CoachInvite from './components/CoachInvite'
import OpenNotifications from './components/OpenNotifications'
import CoachSection from './components/CoachSection'
import FinalCTA from './components/FinalCTA'
import DownloadRedirect from './components/DownloadRedirect'
import MobileDownloadBar from './components/MobileDownloadBar'
import Footer from './components/Footer'

function HomeSections() {
  return (
    <>
      <Hero />
      <Features />
      <Screenshots />
      <CoachSection />
      <Testimonials />
      <FAQ />
      <FinalCTA />
    </>
  )
}

const alternates = {
  home: {
    en: 'https://www.gymjourney.fit/',
    fr: 'https://www.gymjourney.fit/fr',
  },
  blog: {
    en: 'https://www.gymjourney.fit/blog',
    fr: 'https://www.gymjourney.fit/fr/blog',
  },
  contact: {
    en: 'https://www.gymjourney.fit/contact',
    fr: 'https://www.gymjourney.fit/fr/contact',
  },
  accountDeletion: {
    en: 'https://www.gymjourney.fit/account-deletion',
    fr: 'https://www.gymjourney.fit/fr/account-deletion',
  },
  download: {
    en: 'https://www.gymjourney.fit/download',
    fr: 'https://www.gymjourney.fit/fr/download',
  },
  notifications: {
    en: 'https://www.gymjourney.fit/notifications',
    fr: 'https://www.gymjourney.fit/fr/notifications',
  },
  coachInvite: {
    en: 'https://www.gymjourney.fit/coach-invite',
    fr: 'https://www.gymjourney.fit/fr/coach-invite',
  },
}

function AppContent() {
  useScrollToAnchor()
  const { t, setLocaleFromPath } = useLocale()
  const location = useLocation()

  useEffect(() => {
    setLocaleFromPath(location.pathname)
  }, [location.pathname, setLocaleFromPath])

  return (
    <div className="App">
      <Header />
      <main>
        <Routes>
          <Route path="/" element={
            <>
              <SEO
                title={t.seo.home.title}
                description={t.seo.home.description}
                canonical="https://www.gymjourney.fit/"
                alternates={alternates.home}
              />
              <HomeSections />
            </>
          } />
          <Route path="/fr" element={
            <>
              <SEO
                title={t.seo.home.title}
                description={t.seo.home.description}
                canonical="https://www.gymjourney.fit/fr"
                alternates={alternates.home}
              />
              <HomeSections />
            </>
          } />
          <Route
            path="/download"
            element={
              <>
                <SEO title={t.seo.download.title} description={t.seo.download.description} canonical="https://www.gymjourney.fit/download" alternates={alternates.download} noIndex />
                <DownloadRedirect />
              </>
            }
          />
          <Route
            path="/fr/download"
            element={
              <>
                <SEO title={t.seo.download.title} description={t.seo.download.description} canonical="https://www.gymjourney.fit/fr/download" alternates={alternates.download} noIndex />
                <DownloadRedirect />
              </>
            }
          />
          <Route
            path="/blog"
            element={
              <>
                <SEO
                  title={t.seo.blog.title}
                  description={t.seo.blog.description}
                  canonical="https://www.gymjourney.fit/blog"
                  alternates={alternates.blog}
                />
                <Blog />
              </>
            }
          />
          <Route
            path="/fr/blog"
            element={
              <>
                <SEO
                  title={t.seo.blog.title}
                  description={t.seo.blog.description}
                  canonical="https://www.gymjourney.fit/fr/blog"
                  alternates={alternates.blog}
                />
                <Blog />
              </>
            }
          />
          <Route path="/blog/:id" element={<BlogPost />} />
          <Route path="/fr/blog/:id" element={<BlogPost />} />
          <Route
            path="/coach-invite"
            element={
              <>
                <SEO
                  title={t.seo.coachInvite.title}
                  description={t.seo.coachInvite.description}
                  canonical="https://www.gymjourney.fit/coach-invite"
                  alternates={alternates.coachInvite}
                  noIndex
                />
                <CoachInvite />
              </>
            }
          />
          <Route
            path="/fr/coach-invite"
            element={
              <>
                <SEO
                  title={t.seo.coachInvite.title}
                  description={t.seo.coachInvite.description}
                  canonical="https://www.gymjourney.fit/fr/coach-invite"
                  alternates={alternates.coachInvite}
                  noIndex
                />
                <CoachInvite />
              </>
            }
          />
          <Route
            path="/notifications"
            element={
              <>
                <SEO
                  title={t.seo.notifications.title}
                  description={t.seo.notifications.description}
                  canonical="https://www.gymjourney.fit/notifications"
                  alternates={alternates.notifications}
                  noIndex
                />
                <OpenNotifications />
              </>
            }
          />
          <Route
            path="/fr/notifications"
            element={
              <>
                <SEO
                  title={t.seo.notifications.title}
                  description={t.seo.notifications.description}
                  canonical="https://www.gymjourney.fit/fr/notifications"
                  alternates={alternates.notifications}
                  noIndex
                />
                <OpenNotifications />
              </>
            }
          />
          <Route
            path="/contact"
            element={
              <>
                <SEO
                  title={t.seo.contact.title}
                  description={t.seo.contact.description}
                  canonical="https://www.gymjourney.fit/contact"
                  alternates={alternates.contact}
                />
                <Contact />
              </>
            }
          />
          <Route
            path="/fr/contact"
            element={
              <>
                <SEO
                  title={t.seo.contact.title}
                  description={t.seo.contact.description}
                  canonical="https://www.gymjourney.fit/fr/contact"
                  alternates={alternates.contact}
                />
                <Contact />
              </>
            }
          />
          <Route
            path="/account-deletion"
            element={
              <>
                <SEO
                  title={t.deleteAccount.title}
                  description={t.deleteAccount.description}
                  canonical="https://www.gymjourney.fit/account-deletion"
                  alternates={alternates.accountDeletion}
                />
                <DeleteAccount />
              </>
            }
          />
          <Route
            path="/fr/account-deletion"
            element={
              <>
                <SEO
                  title={t.deleteAccount.title}
                  description={t.deleteAccount.description}
                  canonical="https://www.gymjourney.fit/fr/account-deletion"
                  alternates={alternates.accountDeletion}
                />
                <DeleteAccount />
              </>
            }
          />
        </Routes>
      </main>
      <Footer />
      <MobileDownloadBar />
    </div>
  )
}

function App() {
  return (
    <HelmetProvider>
      <LocaleProvider>
        <MotionConfig reducedMotion="user">
          <Router>
            <AppContent />
          </Router>
        </MotionConfig>
      </LocaleProvider>
    </HelmetProvider>
  )
}

export default App
